import { useMemo, useState } from "react";
import { MagnifyingGlass, CaretRight, BookOpen } from "@phosphor-icons/react";
import { articlesForRole } from "@/data/helpContent";

// Renders **bold** and "- " bullet lines within an article body.
function RichText({ text }) {
  const blocks = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) { blocks.push({ type: "ul", items: bullets }); bullets = []; }
  };
  (text || "").split("\n").forEach((line) => {
    const t = line.trim();
    if (t.startsWith("- ")) { bullets.push(t.slice(2)); }
    else { flush(); if (t) blocks.push({ type: "p", text: t }); }
  });
  flush();
  const fmt = (s, key) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={`${key}-${i}`} className="text-[#3C4048] font-semibold">{p.slice(2, -2)}</strong>
        : <span key={`${key}-${i}`}>{p}</span>);
  };
  return (
    <div className="space-y-2.5 text-[#4b5563] text-sm leading-relaxed">
      {blocks.map((b, i) =>
        b.type === "ul"
          ? <ul key={i} className="list-disc pl-5 space-y-1">{b.items.map((it, j) => <li key={j}>{fmt(it, `${i}-${j}`)}</li>)}</ul>
          : <p key={i}>{fmt(b.text, i)}</p>)}
    </div>
  );
}

export function HelpCenter({ role }) {
  const categories = useMemo(() => articlesForRole(role), [role]);
  const [query, setQuery] = useState("");
  const first = categories[0]?.articles[0]?.id || null;
  const [activeId, setActiveId] = useState(first);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return categories;
    return categories
      .map((c) => ({ ...c, articles: c.articles.filter((a) => (a.title + " " + a.body).toLowerCase().includes(q)) }))
      .filter((c) => c.articles.length > 0);
  }, [categories, q]);

  const active = useMemo(() => {
    for (const c of categories) { const a = c.articles.find((x) => x.id === activeId); if (a) return a; }
    return categories[0]?.articles[0] || null;
  }, [categories, activeId]);

  return (
    <div className="flex flex-col lg:flex-row gap-6" data-testid="help-center">
      <aside className="lg:w-72 shrink-0 space-y-4">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af]" />
          <input data-testid="help-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search help…"
            className="w-full bg-white border border-[#e5e7eb] rounded-[10px] pl-9 pr-3 py-2 text-sm text-[#3C4048] focus:outline-none focus:border-[#5FA8B4]" />
        </div>
        <div className="space-y-4">
          {filtered.length === 0 && <p className="text-[#7A9090] text-sm px-1">No articles match “{query}”.</p>}
          {filtered.map((c) => (
            <div key={c.id}>
              <p className="text-[#7A9090] text-[11px] font-bold uppercase tracking-wider px-1 mb-1.5">{c.title}</p>
              <div className="space-y-0.5">
                {c.articles.map((a) => (
                  <button key={a.id} data-testid={`help-article-link-${a.id}`} onClick={() => setActiveId(a.id)}
                    className={`w-full min-w-0 text-left px-3 py-2 rounded-[10px] text-sm flex items-center justify-between gap-2 transition-colors ${active?.id === a.id ? "bg-[#e6f2f4] text-[#3A7A8C] font-semibold" : "text-[#3C4048] hover:bg-[#f5f7f7]"}`}>
                    <span className="truncate min-w-0">{a.title}</span>
                    <CaretRight size={13} className="shrink-0 opacity-50" />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </aside>

      <div className="flex-1 min-w-0 bg-white border border-[#e5e7eb] rounded-[12px] p-6" data-testid="help-article">
        {active ? (
          <>
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={18} className="text-[#5FA8B4]" weight="fill" />
              <h2 className="text-xl font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat" }}>{active.title}</h2>
            </div>
            <RichText text={active.body} />
          </>
        ) : (
          <p className="text-[#7A9090] text-sm">Select an article to read.</p>
        )}
      </div>
    </div>
  );
}
